import { type AccountLinkingCredential, LoginType } from './types';

import {
  ActivityTrackerEventType,
  referralEventTypes,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { useReferralTracker } from '@/modules/activityTracker/useReferralTracker';
import { segmentEventTypes } from '@/modules/externalTracking/types';
import { useExternalTracking } from '@/modules/externalTracking/useExternalTracking';
import { type User } from '@/modules/user/aliases';

export function useLoginTracking() {
  const { sendActivityTrackerEvent, reinitialiseATSession } =
    useActivityTracker();
  const { gtm, branch, segment } = useExternalTracking();
  const trackReferralEvent = useReferralTracker();

  async function sendLoginEvents(user: User, loginType: LoginType) {
    const { id: userId, username, email } = user;

    const segmentIdentifyData = {
      userId: userId.toString(),
      username,
      email,
    };
    const segmentData = {
      userId: userId.toString(),
      username,
    };

    segment.identifyUser(userId.toString(), segmentIdentifyData);
    segment.sendEvent(segmentEventTypes.SIGNED_IN, segmentData);
    gtm.sendEvent({
      category: 'Login',
      action: 'confirm signin',
      label: loginType,
    });

    await Promise.allSettled([
      sendActivityTrackerEvent(
        ActivityTrackerEventType.LOGIN_SUCCESS_ACTION,
        {
          loginType,
          schemaVersion: '3.0',
        },
        { baseEventOverrides: { userId } }
      ),
      branch.identifyUser(userId),
      trackReferralEvent(referralEventTypes.LOGIN, { userId }),
      reinitialiseATSession([], userId),
    ]);
  }

  function sendLoginFailureEvents(loginType: LoginType) {
    /** Should we add a login failure AT event - doesn't currently exist? */
    gtm.sendEvent({
      category: 'Login',
      action: 'failed signin',
      label: loginType,
    });
  }

  function sendLoginSubmitAction(loginType: LoginType) {
    sendActivityTrackerEvent(ActivityTrackerEventType.LOGIN_SUBMIT_ACTION, {
      loginType,
      schemaVersion: '3.0',
    });
  }

  function sendLinkAccountSuccessEvent(credential: AccountLinkingCredential) {
    const thirdParty =
      typeof credential === 'object' &&
      Object.hasOwn(credential, 'apple_access_token')
        ? LoginType.Apple
        : LoginType.Google;

    sendActivityTrackerEvent(ActivityTrackerEventType.LINK_ACCOUNT_SUCCESS, {
      schemaVersion: '1.0',
      thirdParty,
    });
  }

  function sendLoginViewEvent() {
    sendActivityTrackerEvent(ActivityTrackerEventType.WELCOME_VIEW);
  }

  return {
    sendLoginViewEvent,
    sendLoginEvents,
    sendLoginFailureEvents,
    sendLoginSubmitAction,
    sendLinkAccountSuccessEvent,
  };
}
